import { LotacaoAndEstagioSelectFieldModel } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import {
  CondutaEnum,
  DesfechoAtendimentoInput,
  FinalizacaoAtendimentoInput,
  ProcedimentoAtendimentoInput,
  RacionalidadeEmSaudeEnum,
  TipoEncaminhamentoOdontoEnum,
  TipoParticipacaoAtendimentoEnum,
} from 'graphql/types.generated'
import { compact } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { v4 as uuidv4 } from 'uuid'
import { convertNovoAtendimentoModelToInput } from 'view/atendimentos/converter'
import { AtendimentoIndividualRetificacaoModel } from 'view/retificacao-atendimento/model-retificacaoAtendimento'

import { convertAgendarConsultas } from './components/agendar-consulta/converter-agendarConsultaAtendimento'
import { DesfechoAtendimentoFormModel, ManterCidadaoLista } from './components/DesfechoAtendimentoPanel'
import { NotificacaoCasoSuspeitoFieldModel } from './components/NotificacaoCasoSuspeitoField'
import { ProcedimentoSigtapFieldModel } from './components/ProcedimentoSigtapField'
import { FinalizacaoAtendimentoFormModel } from './FinalizacaoAtendimentoForm'
import {
  FinalizacaoAtendimentoQueryModel,
  NotificacaoCasoSuspeitoQueryModel,
  ProcedimentoAtendimentoQueryModel,
} from './model-finalizacao'

export const convertFinalizacaoFormToInput = (
  form: FinalizacaoAtendimentoFormModel,
  showHorarioFinal: boolean = false,
  isParticipacaoTeleconsultaEnabled: boolean = false
): FinalizacaoAtendimentoInput => {
  return (
    form && {
      tipoAtendimento: form.tipoAtendimento,
      atendimentoCompartilhadoLotacaoId: form.atendimentoCompartilhadoLotacao?.id,
      procedimentosAdministrativos: convertProcedimentosAdministrativosFormToInput(form.procedimentosAdministrativos),
      fichaNotificacaoCasoSuspeito: form.fichasNotificacaoCasoSuspeito?.map((ficha) => ficha.fichaNotificacao.id),
      racionalidadeEmSaude: form.racionalidadeEmSaude as RacionalidadeEmSaudeEnum,
      condutas: convertCondutasFormToInput(form.conduta),
      condutasOdonto: convertCondutasOdontoFormToInput(form.condutasOdonto),
      fornecimentosOdonto: form.fornecimento,
      tipoConsultaOdonto: form.tipoConsulta,
      desfechoAtendimento: convertDesfechoAtendimentoFormToInput(form.desfechoAtendimento),
      agendamentoConsultas: convertAgendarConsultas(form.agendamentoConsultas, showHorarioFinal),
      tipoParticipacaoCidadao: convertTipoParticipacaoCidadao(
        form.cidadaoPresente,
        form.tipoParticipacaoCidadao,
        isParticipacaoTeleconsultaEnabled
      ),
      tipoParticipacaoProfissionalConvidado: convertTipoParticipacaoProfissionalConvidado(
        form.atendimentoCompartilhadoLotacao,
        form.tipoParticipacaoProfissionalConvidado,
        form.profissionalNaoListado,
        isParticipacaoTeleconsultaEnabled
      ),
      isProfissionalNaoListado: isParticipacaoTeleconsultaEnabled ? !!form.profissionalNaoListado : undefined,
    }
  )
}
const convertCondutasFormToInput = (condutas: ReadonlyArray<CondutaEnum>) => {
  return condutas?.filter((conduta) => conduta !== CondutaEnum.AGENDAMENTO_PARA_NASF)
}

const convertCondutasOdontoFormToInput = (condutasOdonto: ReadonlyArray<TipoEncaminhamentoOdontoEnum>) => {
  return condutasOdonto?.filter((condutaOdonto) => condutaOdonto !== TipoEncaminhamentoOdontoEnum.AGENDAMENTO_P_NASF)
}

export const convertFinalizacaoQueryToForm = (
  prevValue: FinalizacaoAtendimentoFormModel,
  finalizacao: FinalizacaoAtendimentoQueryModel
): FinalizacaoAtendimentoFormModel => {
  return {
    ...prevValue,
    tipoAtendimento: finalizacao.tipoAtendimento || prevValue.tipoAtendimento,
    conduta: compact([...(prevValue?.conduta ?? []), ...(finalizacao?.condutas ?? [])]).removeDuplicate(),
    racionalidadeEmSaude: finalizacao.racionalidadeSaude,
    atendimentoCompartilhadoLotacao: finalizacao.lotacaoAtendimentoCompartilhado,
    fichasNotificacaoCasoSuspeito: convertFichasNotificacaoCasoSuspeitoQueryToForm(
      finalizacao.notificacoesCasoSuspeito
    ),
    procedimentosAdministrativos: convertProcedimentosAdministrativosQueryToForm(
      prevValue?.procedimentosAdministrativos,
      finalizacao?.procedimentosAtendimento
    ),
    tipoParticipacaoCidadao: finalizacao.tipoParticipacaoCidadao ?? prevValue.tipoParticipacaoCidadao,
    tipoParticipacaoProfissionalConvidado:
      finalizacao.tipoParticipacaoProfissionalConvidado ?? prevValue.tipoParticipacaoProfissionalConvidado,
  }
}

const convertDesfechoAtendimentoFormToInput = (
  desfechoAtendimento: DesfechoAtendimentoFormModel
): DesfechoAtendimentoInput => {
  if (!desfechoAtendimento) {
    return {
      manterCidadaoLista: false,
      atendimento: null,
    }
  }

  const manterCidadaoLista = desfechoAtendimento.manterCidadaoLista === ManterCidadaoLista.SIM

  return {
    manterCidadaoLista: manterCidadaoLista,
    atendimento: manterCidadaoLista ? convertNovoAtendimentoModelToInput(desfechoAtendimento.atendimento) : undefined,
  }
}

export const convertFinalizacaoQueryRetificacaoToForm = (
  atendimentoIndividualRetificacao: AtendimentoIndividualRetificacaoModel
): FinalizacaoAtendimentoFormModel => {
  const {
    procedimentosAtendimento,
    notificacoesCasoSuspeito,
    racionalidadeSaude,
    tipoParticipacaoCidadao,
    tipoParticipacaoProfissionalConvidado,
    lotacaoAtendimentoCompartilhado,
    tipoAtendimento,
    condutas,
  } = atendimentoIndividualRetificacao

  return {
    tipoAtendimento: tipoAtendimento ?? undefined,
    procedimentosAdministrativos: convertProcedimentosAdministrativosQueryRetificacaoToForm(procedimentosAtendimento),
    fichasNotificacaoCasoSuspeito: convertFichasNotificacaoCasoSuspeitoQueryRetificacaoToForm(notificacoesCasoSuspeito),
    racionalidadeEmSaude: racionalidadeSaude ?? undefined,
    cidadaoPresente: tipoParticipacaoCidadao !== TipoParticipacaoAtendimentoEnum.NAO_PARTICIPOU,
    tipoParticipacaoCidadao: tipoParticipacaoCidadao ?? undefined,
    tipoParticipacaoProfissionalConvidado:
      lotacaoAtendimentoCompartilhado && tipoParticipacaoProfissionalConvidado
        ? tipoParticipacaoProfissionalConvidado
        : undefined,
    atendimentoCompartilhadoLotacao: lotacaoAtendimentoCompartilhado ?? undefined,
    conduta: compact(condutas),
  }
}

const convertFichasNotificacaoCasoSuspeitoQueryToForm = (
  items: ReadonlyArray<NotificacaoCasoSuspeitoQueryModel>
): NotificacaoCasoSuspeitoFieldModel[] => {
  return items?.map(({ id, notificacao }) => ({
    _id: uuidv4(),
    editingId: id,
    fichaNotificacao: notificacao,
  }))
}

const convertFichasNotificacaoCasoSuspeitoQueryRetificacaoToForm = (
  items: ReadonlyArray<NotificacaoCasoSuspeitoQueryModel>
): NotificacaoCasoSuspeitoFieldModel[] =>
  items?.map(({ notificacao }) => ({
    _id: uuidv4(),
    fichaNotificacao: notificacao,
  }))

const convertProcedimentosAdministrativosQueryToForm = (
  prevValue: ReadonlyArray<ProcedimentoSigtapFieldModel>,
  items: ReadonlyArray<ProcedimentoAtendimentoQueryModel>
): ProcedimentoSigtapFieldModel[] => {
  return [
    ...(prevValue ?? []),
    ...(items?.map((item) => convertProcedimentoAdminstrativoQueryToForm(item)) ?? []),
  ].removeDuplicate((item) => item.procedimento.id)
}

const convertProcedimentosAdministrativosQueryRetificacaoToForm = (
  items: ReadonlyArray<ProcedimentoAtendimentoQueryModel>
): ProcedimentoSigtapFieldModel[] =>
  items
    ?.map((item) => convertProcedimentoAdminstrativoQueryRetificacaoToForm(item))
    .removeDuplicate((item) => item.procedimento?.id)

const convertProcedimentoAdminstrativoQueryToForm = ({
  id,
  procedimento,
  automatico,
}: ProcedimentoAtendimentoQueryModel): ProcedimentoSigtapFieldModel => {
  return {
    _id: uuidv4(),
    editingId: id,
    procedimento,
    automatico: automatico,
  }
}

export const convertProcedimentoAdminstrativoQueryRetificacaoToForm = ({
  procedimento,
  automatico,
}: ProcedimentoAtendimentoQueryModel): ProcedimentoSigtapFieldModel => ({
  _id: uuidv4(),
  procedimento,
  automatico: automatico,
})

const convertProcedimentosAdministrativosFormToInput = (
  items: ReadonlyArray<ProcedimentoSigtapFieldModel>
): ProcedimentoAtendimentoInput[] => {
  return items?.map((procedimento) => ({
    id: procedimento.procedimento.id,
    cid10PrincipalId: procedimento.procedimento.procedimentoReferencia?.id,
    automatico: procedimento.automatico,
  }))
}

const convertTipoParticipacaoCidadao = (
  cidadaoPresente: boolean,
  tipoParticipacaoCidadao: TipoParticipacaoAtendimentoEnum | undefined,
  isParticipacaoTeleconsultaEnabled: boolean
): TipoParticipacaoAtendimentoEnum | undefined => {
  if (cidadaoPresente === false) {
    return TipoParticipacaoAtendimentoEnum.NAO_PARTICIPOU
  }

  if (isUndefinedOrNull(cidadaoPresente)) {
    return TipoParticipacaoAtendimentoEnum.PRESENCIAL
  }

  if (isParticipacaoTeleconsultaEnabled) return tipoParticipacaoCidadao ?? undefined

  return tipoParticipacaoCidadao ?? TipoParticipacaoAtendimentoEnum.PRESENCIAL
}

const convertTipoParticipacaoProfissionalConvidado = (
  profConvidado: LotacaoAndEstagioSelectFieldModel | undefined,
  tipoParticipacaoProfConvidado: TipoParticipacaoAtendimentoEnum | undefined,
  profissionalNaoListado: boolean | undefined,
  isParticipacaoTeleconsultaEnabled: boolean
): TipoParticipacaoAtendimentoEnum | undefined => {
  const hasProfissionalConvidado = !!profConvidado || !!profissionalNaoListado

  if (isParticipacaoTeleconsultaEnabled) return tipoParticipacaoProfConvidado ?? undefined

  if (!hasProfissionalConvidado) return TipoParticipacaoAtendimentoEnum.NAO_PARTICIPOU

  return tipoParticipacaoProfConvidado ?? TipoParticipacaoAtendimentoEnum.PRESENCIAL
}
